import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Landing } from "@/pages/public/Landing";
import { Terms } from "@/pages/public/Terms";
import { Privacy } from "@/pages/public/Privacy";
import { Dpa } from "@/pages/public/Dpa";
import { BrandPreview } from "@/pages/BrandPreview";
import { SpikePage } from "@/pages/SpikePage";
import { InviteLanding } from "@/pages/candidate/InviteLanding";
import { DemoSession } from "@/pages/candidate/DemoSession";
import { OtpVerify } from "@/pages/candidate/OtpVerify";
import { Personalise } from "@/pages/candidate/Personalise";
import { SessionReveal } from "@/pages/candidate/SessionReveal";
import { Setup } from "@/pages/candidate/Setup";
import { NotificationPrompt } from "@/pages/candidate/NotificationPrompt";
import { PrepLoading } from "@/pages/candidate/PrepLoading";
import { LiveSession } from "@/pages/candidate/LiveSession";
import { Scorecard } from "@/pages/candidate/Scorecard";
import { Home } from "@/pages/candidate/Home";
import { Briefs } from "@/pages/candidate/Briefs";
import { Brief } from "@/pages/candidate/Brief";
import { Settings } from "@/pages/candidate/Settings";
import { CandidateProfile } from "@/pages/candidate/CandidateProfile";
import { KB } from "@/pages/candidate/KB";
import { RecruiterLogin } from "@/pages/recruiter/RecruiterLogin";
import { RecruiterRegister } from "@/pages/recruiter/RecruiterRegister";
import { RecruiterForgot } from "@/pages/recruiter/RecruiterForgot";
import { RecruiterReset } from "@/pages/recruiter/RecruiterReset";
import { RecruiterDashboard } from "@/pages/recruiter/RecruiterDashboard";
import { RecruiterCandidates } from "@/pages/recruiter/RecruiterCandidates";
import { RecruiterCandidateDetail } from "@/pages/recruiter/RecruiterCandidateDetail";
import { RecruiterSendInvite } from "@/pages/recruiter/RecruiterSendInvite";
import { RecruiterSettings } from "@/pages/recruiter/RecruiterSettings";
import { RecruiterBilling } from "@/pages/recruiter/RecruiterBilling";
import { RecruiterReports } from "@/pages/recruiter/RecruiterReports";
import { RecruiterVerifyEmailPending } from "@/pages/recruiter/RecruiterVerifyEmailPending";
import { RecruiterScorecard } from "@/pages/recruiter/RecruiterScorecard";
import { CandidatePortalLogin } from "@/pages/candidate/CandidatePortalLogin";
import { CandidatePortal } from "@/pages/candidate/CandidatePortal";
import { EraseConfirm } from "@/pages/candidate/EraseConfirm";
import { FindMyPortal } from "@/pages/public/FindMyPortal";
import { OnboardingWelcome } from "@/pages/recruiter/OnboardingWelcome";
import { BrandSetup } from "@/pages/recruiter/BrandSetup";
import { StagingBanner } from "@/components/StagingBanner";

// Router — no basename; SPA served from origin root (/).
export default function App() {
  return (
    <>
      <StagingBanner />
      <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/brand-preview" element={<BrandPreview />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/dpa" element={<Dpa />} />
        <Route path="/spike" element={<SpikePage />} />

        {/* Candidate invite journey — IC-237 onboarding flow */}
        <Route path="/invite/:token" element={<InviteLanding />} />
        <Route path="/invite/:token/demo" element={<DemoSession />} />
        <Route path="/invite/:token/verify" element={<OtpVerify />} />
        <Route path="/invite/:token/personalise" element={<Personalise />} />
        <Route path="/invite/:token/reveal" element={<SessionReveal />} />
        <Route path="/invite/:token/setup" element={<Setup />} />
        <Route path="/invite/:token/notify/:sessionId" element={<NotificationPrompt />} />
        <Route path="/invite/:token/prep/:sessionId" element={<PrepLoading />} />
        <Route path="/invite/:token/session/:sessionId" element={<LiveSession />} />
        <Route path="/invite/:token/scorecard/:sessionId" element={<Scorecard />} />
        <Route path="/invite/:token/home" element={<Home />} />
        <Route path="/invite/:token/briefs" element={<Briefs />} />
        <Route path="/invite/:token/brief" element={<Brief />} />
        <Route path="/invite/:token/settings" element={<Settings />} />
        <Route path="/invite/:token/profile" element={<CandidateProfile />} />
        {/* Chat with Coach (B4.2) is DELIBERATELY not routed. Development was
            paused; candidates must not reach it yet. Nothing in the UI linked
            to it, but the route meant anyone typing /invite/<token>/chat got
            in, so the route is what actually gated it.

            The page (pages/candidate/CoachChat.tsx) and the API endpoint
            (POST /api/v1/invite/<token>/chat) are both kept working, so
            resuming is a one-line change: restore this Route and its import.

            Do not "fix" this by re-adding the route. On 2026-09-01 the absence
            of chat was twice read as a bug and repaired; it is a product
            decision. Ask before restoring. */}
        <Route path="/invite/:token/kb" element={<KB />} />
        <Route path="/invite/:token/erase" element={<EraseConfirm />} />

        {/* Candidate portal (tokenless) */}
        <Route path="/candidate/login" element={<CandidatePortalLogin />} />
        <Route path="/candidate/home" element={<CandidatePortal />} />

        {/* Find my portal */}
        <Route path="/find-my-portal" element={<FindMyPortal />} />

        {/* Recruiter onboarding + brand setup */}
        <Route path="/onboarding/welcome" element={<OnboardingWelcome />} />
        <Route path="/onboarding" element={<OnboardingWelcome />} />
        <Route path="/agency/brand-setup" element={<BrandSetup />} />

        {/* Recruiter auth */}
        <Route path="/auth/login" element={<RecruiterLogin />} />
        <Route path="/auth/register" element={<RecruiterRegister />} />
        <Route path="/auth/forgot" element={<RecruiterForgot />} />
        <Route path="/auth/reset/:token" element={<RecruiterReset />} />
        <Route path="/auth/verify-email-pending" element={<RecruiterVerifyEmailPending />} />

        {/* Recruiter portal */}
        <Route path="/coach/recruiter" element={<RecruiterDashboard />} />
        <Route path="/coach/recruiter/candidates" element={<RecruiterCandidates />} />
        <Route path="/coach/recruiter/candidate/:email" element={<RecruiterCandidateDetail />} />
        <Route path="/coach/recruiter/invite/new" element={<RecruiterSendInvite />} />
        <Route path="/coach/recruiter/settings" element={<RecruiterSettings />} />
        <Route path="/coach/recruiter/billing" element={<RecruiterBilling />} />
        <Route path="/coach/recruiter/reports" element={<RecruiterReports />} />
        <Route path="/coach/recruiter/scorecard/:sessionId" element={<RecruiterScorecard />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </>
  );
}
